import React, { useState, useMemo } from 'react';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { LegacyGrid as Grid } from '../../../../elements';
import { V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../../../theme/v4Compat';
import Alert from '@mui/material/Alert';
import { useTranslation } from 'react-i18next';
import { HealthSystemDomainImportResultImportDataFromCsvFileResult } from '../../../../api/HealthSystemApi';
import { tokens } from '../../../../theme/tokens';

interface IProps {
  result: HealthSystemDomainImportResultImportDataFromCsvFileResult;
  onImportClick?: () => void;
}

const ImportResults: React.FC<IProps> = ({ result, onImportClick }) => {
  const { t } = useTranslation();
  const hasErrors = result.errors && result.errors.length > 0;
  const isValid = result.isSuccess && !hasErrors;

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filter, setFilter] = useState('all');

  // Validation errors collapse state (collapsed by default)
  const [errorsExpanded, setErrorsExpanded] = useState(false);

  // Create error mapping for quick lookup
  const errorsByRow = useMemo(() => {
    const errors: { [key: number]: Array<{ field: string; message: string }> } = {};
    const data = [...(result.errors || []), ...(result.warnings || [])];
    if (data) {
      data.forEach((item) => {
        if (!errors[item.rowIndex]) {
          errors[item.rowIndex] = [];
        }
        errors[item.rowIndex].push({ field: item.field, message: item.message });
      });
    }
    return errors;
  }, [result.errors]);

  // Determine row status based on errors
  const getRowStatus = (rowIndex: number) => {
    if (errorsByRow[rowIndex + 1]) {
      return 'invalid';
    }
    return 'valid';
  };

  // Filter records based on selection
  const filteredRecords = useMemo(() => {
    if (!result.records) return [];

    return result.records.filter((_, idx) => {
      const status = getRowStatus(idx);
      if (filter === 'all') return true;
      if (filter === 'valid') return status === 'valid';
      if (filter === 'invalid') return status === 'invalid';
      return true;
    });
  }, [result.records, filter, errorsByRow]);

  // Calculate pagination
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRecords = filteredRecords.slice(startIndex, endIndex);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (event: SelectChangeEvent<unknown>) => {
    setFilter(event.target.value as string);
    setPage(0);
  };

  const handleRowClick = (rowIndex: number) => {
    const actualRowIndex = result.records?.indexOf(filteredRecords[rowIndex - startIndex]) || 0;
    const errors = errorsByRow[actualRowIndex + 1];
    if (errors && errors.length > 0) {
      // Scroll to errors section
      const errorSection = document.getElementById('validation-errors');
      if (errorSection) {
        errorSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <Paper style={{ padding: 24, marginTop: 16 }}>
      {/* Summary Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
        {isValid ? (
          <CheckCircleIcon style={{ color: '#4caf50', marginRight: 8 }} />
        ) : (
          <ErrorIcon style={{ color: '#f44336', marginRight: 8 }} />
        )}
        <Typography variant="h6">
          {result.fileName
            ? t('f-import-results-title', { fileName: result.fileName })
            : t('f-import-results-title-default')}
        </Typography>
      </Box>

      {/* Status Alert */}
      {isValid ? (
        <Alert severity="success" style={{ marginBottom: 16 }}>
          {t('f-import-validation-success-alert', { validRows: result.validRows })}
        </Alert>
      ) : (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {t('f-import-validation-error-alert', {
            errorMessage: result.errorMessage || `Validation failed with ${result.errors?.length || 0} error(s).`
          })}
        </Alert>
      )}

      {/* Summary Stats */}
      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="textPrimary">
              {result.totalRecords}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('f-import-stats-total-records')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" style={{ color: '#4caf50' }}>
              {result.validRows}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('f-import-stats-valid-rows')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="primary">
              {result.createdCount || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('f-import-stats-created')}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" style={{ color: '#f44336' }}>
              {result.skippedCount || 0}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('f-import-stats-skipped')}
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {/* Import Action Button */}
      {isValid && onImportClick && (
        <Box sx={{ marginBottom: 2 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onImportClick}
            startIcon={<CheckCircleIcon />}
          >
            {t('f-import-btn-import-records', { validRows: result.validRows })}
          </Button>
        </Box>
      )}

      <Divider style={{ margin: '16px 0' }} />

      {/* Data Preview with Filtering and Pagination */}
      {result.records && result.records.length > 0 && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
            <Typography variant="h6">
              {t('f-import-data-preview-title', {
                filteredCount: filteredRecords.length,
                filter: filter !== 'all' ? filter : ''
              })}
            </Typography>

            <FormControl size="small" style={{ minWidth: 120 }} variant="standard">
              <InputLabel>{t('f-import-data-preview-filter-label')}</InputLabel>
              <Select value={filter} onChange={handleFilterChange}>
                <MenuItem value="all">
                  {t('f-import-data-preview-filter-all', { count: result.records.length })}
                </MenuItem>
                <MenuItem value="valid">
                  {t('f-import-data-preview-filter-valid', {
                    count: result.records.length - (result.errors?.length || 0) - (result.warnings?.length || 0)
                  })}
                </MenuItem>
                <MenuItem value="invalid">
                  {t('f-import-data-preview-filter-invalid', { count: result.errors?.length || 0 })}
                </MenuItem>
                <MenuItem value="warnings">
                  {t('f-import-data-preview-filter-warnings', { count: result.warnings?.length || 0 })}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Add horizontal scroll container for the data table */}
          <Box style={{ overflowX: 'auto', marginTop: 8 }}>
            <Table size="small" style={{ minWidth: 650 }} sx={V4_TABLE}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ minWidth: 80 }}>
                    <strong>{t('f-import-validation-errors-row')}</strong>
                  </TableCell>
                  {result.columns.map((col) => (
                    <TableCell key={col} style={{ minWidth: 120, whiteSpace: 'nowrap' }}>
                      <strong>{col}</strong>
                    </TableCell>
                  ))}
                  <TableCell style={{ minWidth: 100 }}>
                    <strong>{t('f-import-data-preview-status')}</strong>
                  </TableCell>
                  <TableCell style={{ minWidth: 200 }}>
                    <strong>{t('f-import-data-preview-errors')}</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRecords.map((row) => {
                  const actualRowIndex = result.records?.indexOf(row) || 0;
                  const rowErrors = errorsByRow[actualRowIndex + 1] || [];
                  const status = getRowStatus(actualRowIndex);

                  return (
                    <TableRow
                      key={actualRowIndex}
                      id={`data-row-${actualRowIndex + 1}`}
                      style={{
                        backgroundColor:
                          status === 'invalid'
                            ? '#ffebee'
                            : status === 'valid'
                              ? '#e8f5e8'
                              : status === 'warning'
                                ? '#fff8e1'
                                : 'inherit'
                      }}
                    >
                      <TableCell style={{ minWidth: 80 }}>
                        <strong>#{actualRowIndex + 1}</strong>
                      </TableCell>
                      {result.columns.map((col) => (
                        <TableCell
                          key={col}
                          style={{
                            minWidth: 120,
                            whiteSpace: 'nowrap',
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {row[col] !== undefined ? String(row[col]) : ''}
                        </TableCell>
                      ))}
                      <TableCell style={{ minWidth: 100 }}>
                        <Chip
                          size="small"
                          label={
                            status === 'valid'
                              ? t('f-import-data-preview-valid')
                              : status === 'invalid'
                                ? t('f-import-validation-errors-invalid')
                                : t('f-import-data-preview-warnings')
                          }
                          color={status === 'valid' ? 'primary' : 'secondary'}
                          variant={status === 'valid' ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell style={{ minWidth: 200 }}>
                        {rowErrors.length > 0 && (
                          <Box>
                            {rowErrors.map((error, errorIdx) => (
                              <Chip
                                key={errorIdx}
                                size="small"
                                label={`${error.field}: ${error.message.substring(0, 30)}${error.message.length > 30 ? '...' : ''}`}
                                color="secondary"
                                style={{ marginRight: 4, marginBottom: 2 }}
                                onClick={() => {
                                  // Expand errors section if collapsed
                                  if (!errorsExpanded) {
                                    setErrorsExpanded(true);
                                  }
                                  // Scroll to error element after a short delay to allow expansion
                                  setTimeout(
                                    () => {
                                      const errorElement = document.getElementById(`error-row-${actualRowIndex + 1}`);
                                      if (errorElement) {
                                        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                      }
                                    },
                                    errorsExpanded ? 0 : 300
                                  ); // Delay if expanding
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>

          {/* Move pagination outside scrollable area and align to left */}
          <Box style={{ marginTop: 8 }} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <TablePagination
              component="div"
              rowsPerPageOptions={[5, 10, 25, 50]}
              count={filteredRecords.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              style={{ padding: 0 }}
            />
          </Box>
        </>
      )}

      {/* Error Details - Moved after data table and made collapsible */}
      {hasErrors && (
        <>
          <Divider style={{ margin: '16px 0' }} />
          <Box id="validation-errors" sx={{ marginBottom: 3 }}>
            <Box
              style={{ cursor: 'pointer' }}
              onClick={() => setErrorsExpanded(!errorsExpanded)}
              title={t('f-import-validation-errors-expand')}
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <ErrorIcon style={{ verticalAlign: 'middle', marginRight: 8, color: '#f44336' }} />
              <Typography variant="h6" style={{ color: '#f44336', flexGrow: 1 }}>
                {t('f-import-validation-errors-title', { count: result.errors.length })}
              </Typography>
              <IconButton
                size="small"
                style={{
                  transform: errorsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}
                sx={V4_ICON_BUTTON_SMALL}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Box>

            <Collapse in={errorsExpanded}>
              <Box sx={{ marginTop: 2 }}>
                <Table size="small" sx={V4_TABLE}>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('f-import-validation-errors-row')}</TableCell>
                      <TableCell>{t('f-import-validation-errors-field')}</TableCell>
                      <TableCell>{t('f-import-validation-errors-message')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.errors.map((error, idx) => (
                      <TableRow key={idx} id={`error-row-${error.rowIndex}`}>
                        <TableCell
                          style={{ cursor: 'pointer', color: tokens.primary.main }}
                          onClick={() => handleRowClick(error.rowIndex)}
                        >
                          #{error.rowIndex}
                        </TableCell>
                        <TableCell>{error.field}</TableCell>
                        <TableCell style={{ color: '#f44336' }}>{error.message}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default ImportResults;
